import React, { useState } from 'react';
import { 
  Card, 
  Descriptions, 
  Tag, 
  Button, 
  Space, 
  Row, 
  Col, 
  Modal, 
  message,
  Divider,
  Typography,
  Form,
  Input
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined,
  PauseCircleOutlined,
  SaveOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { PlanogramTemplate } from '../../../../types/plan-template';
import { PlanogramTemplateService } from '../../../../services/planogramTemplateService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface BasicInformationProps {
  template: PlanogramTemplate;
  onEdit?: (template: PlanogramTemplate) => void;
  onDelete?: (templateId: string) => void;
  onReload?: () => Promise<void>;
}

const BasicInformation: React.FC<BasicInformationProps> = ({ 
  template, 
  onEdit, 
  onDelete, 
  onReload 
}) => {
  const { t } = useTranslation();
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [basicInfoForm] = Form.useForm();

  const getValidationStatusTranslation = (status: string) => {
    const statusKey = status.toLowerCase().replace(' ', '');
    return t(`planTemplate.detail.basicInfo.validationStatusValues.${statusKey}`, { defaultValue: status });
  };

  React.useEffect(() => {
    basicInfoForm.setFieldsValue({
      name: template.name,
      description: template.description,
      remark: template.remark
    });
  }, [template, basicInfoForm]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Published': return 'success';
      case 'Draft': return 'processing';
      case 'Disabled': return 'default';
      default: return 'default';
    }
  };

  const getValidationStatusColor = (status: string) => {
    switch (status) {
      case 'Validation Passed': return 'success';
      case 'Validation Failed': return 'error';
      case 'Not Validated': return 'warning';
      default: return 'default';
    }
  };

  const handleStatusChange = async (newStatus: 'Published' | 'Disabled') => {
    try {
      const actionText = newStatus === 'Published' ? t('planTemplate.detail.basicInfo.publish') : t('planTemplate.detail.basicInfo.disable');
      const confirmText = newStatus === 'Published' 
        ? t('planTemplate.detail.basicInfo.publishMessage', { name: template.name })
        : t('planTemplate.detail.basicInfo.disableMessage', { name: template.name });

      Modal.confirm({
        title: `${t('common.confirm')}${actionText}`,
        content: confirmText,
        okText: actionText,
        cancelText: t('common.cancel'),
        onOk: async () => {
          try {
            const response = await PlanogramTemplateService.batchUpdateStatus([template.id], newStatus);
            
            if (response.success) {
              message.success(newStatus === 'Published' 
                ? t('planTemplate.detail.basicInfo.publishSuccess')
                : t('planTemplate.detail.basicInfo.disableSuccess'));
              if (onReload) {
                await onReload();
              }
            } else {
              message.error(response.message || (newStatus === 'Published' 
                ? t('planTemplate.detail.basicInfo.publishFailed')
                : t('planTemplate.detail.basicInfo.disableFailed')));
            }
          } catch (err) {
            message.error(newStatus === 'Published' 
              ? t('planTemplate.detail.basicInfo.publishError')
              : t('planTemplate.detail.basicInfo.disableError'));
            console.error('Status update error:', err);
          }
        }
      });
    } catch (err) {
      message.error(t('planTemplate.detail.basicInfo.statusUpdateError'));
      console.error('Status update error:', err);
    }
  };

  const handleBasicInfoSave = async () => {
    try {
      const values = await basicInfoForm.validateFields();
      
      const updateRequest = {
        id: template.id,
        name: values.name,
        description: values.description,
        remark: values.remark
      };

      const response = await PlanogramTemplateService.updateTemplateBasicInfo(template.id, updateRequest);
      
      if (response.success) {
        message.success(t('planTemplate.detail.basicInfo.basicInfoUpdateSuccess'));
        setEditingBasicInfo(false);
        if (onReload) {
          await onReload();
        }
      } else {
        message.error(response.message || t('planTemplate.detail.basicInfo.basicInfoUpdateFailed'));
      }
    } catch (err) {
      console.error('Basic info update error:', err);
    }
  };

  const handleBasicInfoCancel = () => {
    setEditingBasicInfo(false);
    basicInfoForm.setFieldsValue({
      name: template.name,
      description: template.description,
      remark: template.remark
    });
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(template);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('planTemplate.detail.basicInfo.confirmDelete'),
      content: t('planTemplate.detail.basicInfo.deleteMessage', { name: template.name }),
      okText: t('planTemplate.detail.basicInfo.delete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await PlanogramTemplateService.deleteTemplate(template.id);
          
          if (response.success) {
            message.success(t('planTemplate.detail.basicInfo.deleteSuccess'));
            if (onDelete) {
              onDelete(template.id);
            }
          } else {
            message.error(response.message || t('planTemplate.detail.basicInfo.deleteFailed'));
          }
        } catch (err) {
          message.error(t('planTemplate.detail.basicInfo.deleteError'));
          console.error('Delete error:', err);
        }
      }
    });
  };

  return (
    <Card 
      title={
        <Space>
          <InfoCircleOutlined />
          {t('planTemplate.detail.basicInfo.title')}
        </Space>
      }
      extra={
        <Space>
          {!editingBasicInfo ? (
            <>
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={() => setEditingBasicInfo(true)}
                disabled={template.status === 'Published'}
              >
                {t('planTemplate.detail.basicInfo.editBasicInfo')}
              </Button>
              {/*<Button */}
              {/*  type="primary" */}
              {/*  icon={<SettingOutlined />} */}
              {/*  onClick={handleEdit}*/}
              {/*  disabled={template.status === 'Published'}*/}
              {/*>*/}
              {/*  配置编辑*/}
              {/*</Button>*/}

              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleDelete}
                disabled={template.status !== 'Draft'}
              >
                {t('planTemplate.detail.basicInfo.delete')}
              </Button>
            </>
          ) : (
            <>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleBasicInfoSave}
              >
                {t('planTemplate.detail.basicInfo.save')}
              </Button>
              <Button 
                icon={<CloseOutlined />} 
                onClick={handleBasicInfoCancel}
              >
                {t('planTemplate.detail.basicInfo.cancel')}
              </Button>
            </>
          )}
        </Space>
      }
    >
      {!editingBasicInfo ? (
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.templateCode')}>
                <Text strong code>{template.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.templateName')}>
                <Text strong>{template.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.planType')}>
                <Tag color="blue" icon={<BranchesOutlined />}>{template.planType}</Tag>
              </Descriptions.Item>
              {/* <Descriptions.Item label={t('planTemplate.detail.basicInfo.businessActivity')}>
                <Tag color="purple">{template.businessActivity}</Tag>
              </Descriptions.Item> */}
              <Descriptions.Item label={t('planTemplate.templateOwner')}>
                <Text>{template.owner || '-'}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.status')}>
                <Space>
                  <Tag color={getStatusColor(template.status)}>{template.status}</Tag>
                  {template.status === 'Draft' && (
                    <Button 
                      size="small" 
                      type="link" 
                      icon={<PlayCircleOutlined />}
                      onClick={() => handleStatusChange('Published')}
                      disabled={template.validationStatus !== 'Validation Passed'}
                    >
                      {t('planTemplate.detail.basicInfo.publish')}
                    </Button>
                  )}
                  {template.status === 'Published' && (
                    <Button 
                      size="small" 
                      type="link" 
                      icon={<PauseCircleOutlined />}
                      onClick={() => handleStatusChange('Disabled')}
                    >
                      {t('planTemplate.detail.basicInfo.disable')}
                    </Button>
                  )}
                  {template.status === 'Disabled' && (
                    <Button 
                      size="small" 
                      type="link" 
                      icon={<PlayCircleOutlined />}
                      onClick={() => handleStatusChange('Published')}
                    >
                      {t('planTemplate.detail.basicInfo.enable')}
                    </Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.validationStatus')}>
                <Tag color={getValidationStatusColor(template.validationStatus)}>
                  {getValidationStatusTranslation(template.validationStatus)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.createTime')}>
                <Text>{dayjs(template.gmtCreate).format('YYYY-MM-DD HH:mm:ss')}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.updateTime')}>
                <Text>{dayjs(template.gmtModified).format('YYYY-MM-DD HH:mm:ss')}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.basicInfo.creator')}>
                <Text>{template.creatorName}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      ) : (
        <Form form={basicInfoForm} layout="vertical">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={t('planTemplate.detail.basicInfo.templateName')}
                name="name"
                rules={[
                  { required: true, message: t('planTemplate.detail.basicInfo.inputTemplateName') },
                  { min: 2, max: 50, message: t('planTemplate.detail.basicInfo.templateNameLength') }
                ]}
              >
                <Input placeholder={t('planTemplate.detail.basicInfo.templateNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>{t('planTemplate.detail.basicInfo.templateCode')}：</Text>
                <Text code>{template.code}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>{t('planTemplate.detail.basicInfo.notModifiable')}</Text>
              </div>
            </Col>
          </Row>
          <Form.Item
            label={t('planTemplate.detail.basicInfo.description')}
            name="description"
            rules={[
              { required: true, message: t('planTemplate.detail.basicInfo.inputDescription') },
              { min: 10, max: 500, message: t('planTemplate.detail.basicInfo.descriptionLength') }
            ]}
          >
            <TextArea rows={4} placeholder={t('planTemplate.detail.basicInfo.descriptionPlaceholder')} />
          </Form.Item>
          <Form.Item
            label={t('planTemplate.detail.basicInfo.remark')}
            name="remark"
          >
            <TextArea rows={2} placeholder={t('planTemplate.detail.basicInfo.remarkPlaceholder')} />
          </Form.Item>
        </Form>
      )}
      
      {!editingBasicInfo && template.description && (
        <>
          <Divider />
          <div>
            <Text strong>{t('planTemplate.detail.basicInfo.description')}：</Text>
            <Paragraph>{template.description}</Paragraph>
          </div>
        </>
      )}
      
      {!editingBasicInfo && template.remark && (
        <div>
          <Text strong>{t('planTemplate.detail.basicInfo.remark')}：</Text>
          <Paragraph>{template.remark}</Paragraph>
        </div>
      )}
    </Card>
  );
};

export default BasicInformation;